export enum ContratStatut {
  EN_COURS = 'EN_COURS',
  RESILIE = 'RESILIE',
  EN_ATTENTE = 'EN_ATTENTE',
}

export enum CotisationPeriode {
  MENSUEL = 'MENSUEL',
  ANNUEL = 'ANNUEL',
}

export enum TypeIndemnisation {
  FORFAITAIRE = 'FORFAITAIRE',
  INDEMNITAIRE = 'INDEMNITAIRE',
}

export interface BasePrevoyanceContrat {
  id: number;
  accountId: number;
  productId: number;
  productName?: string;
  assureurName?: string;
  ownerId: number;
  titulaireName?: string;
  cotisation: number;
  numeroContrat?: string;
  devisId?: number;
  dateSouscription?: string;
  dateDebut?: string;
  dateFin?: string;
  statut?: ContratStatut;
  prAssetsInsuranceId?: number;
  createdByUserId?: number;
  commentaire?: string;
  gedDocumentIds?: number[];
  createdBy?: string;
  lastModifiedBy?: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

export interface AssuranceEmprunteurContratDto extends BasePrevoyanceContrat {
  quotiteDeces?: number;
  quotitePtia?: number;
  quotiteIpt?: number;
  quotiteItt?: number;
  quotiteIpp?: number;
  quotitePe?: number;
  franchiseIttJours?: number;
  franchisePeJours?: number;
  typeIndemnisation?: TypeIndemnisation;
  priseEnChargeDosPsy?: boolean;
}

export interface MutuelleContratDto extends BasePrevoyanceContrat {
  cotisationPeriode: CotisationPeriode;
  // Hospitalisation
  hospitalisationHonorairesOptam?: number;
  hospitalisationHonorairesNonOptam?: number;
  hospitalisationChambreParticuliere?: number;
  hospitalisationPrimeMaternite?: string;
  // Optique
  optiqueLunettes?: number;
  optiqueLentilles?: number;
  optiquePlafond?: number;
  optiqueChirurgieLaser?: number;
  // Dentaire
  dentaireProthese?: number;
  dentaireOrthodontieAdulte?: string;
  dentaireOrthodontieEnfant?: string;
  // Pharmacie
  pharmaciePriseEnCharge?: number;
  // Médecines douces
  medecinesDouces?: string;
  // Consultations
  consultationsOptamCas?: number;
  consultationsNonOptamNonCas?: number;
  // Cotisations
  cotisationsMontant?: number;
}

export interface PrevoyanceContratDto extends BasePrevoyanceContrat {
  cotisationPeriode: CotisationPeriode;
  enComplementRegimeObligatoire?: boolean;
  ittMontantMensuel?: number;
  ittFraisGenerauxMensuels?: number;
  ittFranchises?: string;
  ittRenfortDos?: boolean;
  ittAffectionPsy?: boolean;
  ittDelaisAttente?: string;
  ittMiTempsTherapeutique?: boolean;
  invaliditeMontantMensuel?: number;
  invaliditeBareme?: string;
  invaliditeSeuilDeclenchement?: number;
  decesPtiaMontant?: number;
  cotisationMensuelle?: string;
  cotisationMadelin?: string;
}

export const PREVOYANCE_TYPE_CONFIG = {
  'assurance-emprunteur': {
    path: 'assurance-emprunteur',
    label: 'Assurance emprunteur',
    addLabel: "Ajouter un contrat d'assurance emprunteur",
    gedCategory: 'CONTRACT',
    gedDocumentType: 'CONTRAT_ASSURANCE_EMPRUNTEUR',
    gedProductCategory: 'ASSURANCE_EMPRUNTEUR',
    gedLabelPrefix: 'Contrat-AE',
  },
  'mutuelle': {
    path: 'mutuelle',
    label: 'Mutuelle',
    addLabel: 'Ajouter un contrat de mutuelle',
    gedCategory: 'CONTRACT',
    gedDocumentType: 'CONTRAT_MUTUELLE',
    gedProductCategory: 'MUTUELLE',
    gedLabelPrefix: 'Contrat-Mutuelle',
  },
  'prevoyance': {
    path: 'prevoyance-personnelle',
    label: 'Prévoyance',
    addLabel: 'Ajouter un contrat de prévoyance',
    gedCategory: 'CONTRACT',
    gedDocumentType: 'CONTRAT_PREVOYANCE',
    gedProductCategory: 'PREVOYANCE',
    gedLabelPrefix: 'Contrat-Prevoyance',
  },
} as const;

export type PrevoyanceType = keyof typeof PREVOYANCE_TYPE_CONFIG;

export function isContratActif(contrat: BasePrevoyanceContrat): boolean {
  return !contrat.statut || contrat.statut === ContratStatut.EN_COURS;
}

export function statutLabel(contrat: BasePrevoyanceContrat): string {
  switch (contrat.statut) {
    case ContratStatut.EN_COURS:
      return 'En cours';
    case ContratStatut.EN_ATTENTE:
      return 'En attente';
    case ContratStatut.RESILIE:
      return 'Résilié';
    default:
      return 'En cours';
  }
}

export function statutCssClass(contrat: BasePrevoyanceContrat): string {
  switch (contrat.statut) {
    case ContratStatut.EN_COURS:
      return 'status-en-cours';
    case ContratStatut.EN_ATTENTE:
      return 'status-en-attente';
    case ContratStatut.RESILIE:
      return 'status-resilie';
    default:
      return 'status-en-cours';
  }
}

export function toMonthlyCotisation(
  contrat: BasePrevoyanceContrat,
  type: PrevoyanceType,
): number {
  const cotisation = contrat.cotisation || 0;
  if (type === 'assurance-emprunteur') {
    return cotisation / 12;
  }
  const periode = (contrat as MutuelleContratDto | PrevoyanceContratDto)
    .cotisationPeriode;
  return periode === CotisationPeriode.ANNUEL ? cotisation / 12 : cotisation;
}
